import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button, Modal, Spinner, Table } from 'react-bootstrap';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { NoDataComponent } from '../components/NoDataComponent';
import { User } from '../models/table-models';
import {
    getInventoryRevision,
    getInventoryRevisionDifferences,
    getInventoryRevisionDistributors,
    getInventoryRevisionOverwriteBlockers,
    getPendingInventoryRevisionDistributorIds,
    inventoryRevisionObservationsReadEvent,
    keepInventoryRevision,
    InventoryRevisionDifferenceRecord,
    InventoryRevisionResponse,
    InventoryRevisionRow,
    markInventoryRevisionHistoryRead,
    overwriteInventoryRevision,
} from '../utils/clients-hooks';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../components/CustomToastContent';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { getGeneratedBatchCode } from '../utils/batch-code';

export const Revisions = () => {
    const { t, i18n } = useTranslation('translation', { keyPrefix: 'common' });
    const [distributors, setDistributors] = useState<User[]>([]);
    const [isLoadingDistributors, setIsLoadingDistributors] = useState(true);
    const [selectedDistributor, setSelectedDistributor] = useState<User>();
    const [revision, setRevision] = useState<InventoryRevisionResponse>();
    const [isLoadingRevision, setIsLoadingRevision] = useState(false);
    const [revisionActionLoading, setRevisionActionLoading] = useState<'overwrite' | 'keep'>();
    const [showOverwriteConfirm, setShowOverwriteConfirm] = useState(false);
    const [showSavedDifferences, setShowSavedDifferences] = useState(false);
    const [isLoadingSavedDifferences, setIsLoadingSavedDifferences] = useState(false);
    const [savedDifferences, setSavedDifferences] = useState<InventoryRevisionDifferenceRecord[]>([]);
    const [historyDistributor, setHistoryDistributor] = useState<User>();
    const [pendingRevisionDistributorIds, setPendingRevisionDistributorIds] = useState<Set<number>>(new Set());
    const items = useMemo(() => distributors.filter((distributor) => !!distributor.id), [distributors]);

    const refreshPendingRevisionDistributorIds = useCallback((forceRefresh = false) => {
        return getPendingInventoryRevisionDistributorIds(forceRefresh)
            .then((distributorIds) => setPendingRevisionDistributorIds(new Set(distributorIds)));
    }, []);

    useEffect(() => {
        let active = true;

        setIsLoadingDistributors(true);
        getInventoryRevisionDistributors()
            .then((response) => {
                if (active) setDistributors(response.items);
            })
            .then(() => refreshPendingRevisionDistributorIds(true))
            .finally(() => {
                if (active) setIsLoadingDistributors(false);
            });

        return () => {
            active = false;
        };
    }, [refreshPendingRevisionDistributorIds]);

    const canOverwriteSelectedDistributorRevision = async (distributorId: number) => {
        const overwriteBlockers = await getInventoryRevisionOverwriteBlockers(distributorId);

        if (overwriteBlockers.hasPendingInventoryReceipts || overwriteBlockers.hasReturnedItemsToWarehouse) {
            const blockerMessages = [
                overwriteBlockers.hasPendingInventoryReceipts
                    ? t('revisionOverwriteBlockedByPendingInventoryReceipts', {
                        count: overwriteBlockers.pendingInventoryReceiptsCount,
                    })
                    : undefined,
                overwriteBlockers.hasReturnedItemsToWarehouse
                    ? t('revisionOverwriteBlockedByReturnedItems', {
                        count: overwriteBlockers.returnedItemsToWarehouseCount,
                    })
                    : undefined,
            ].filter(Boolean) as string[];

            toast.error(
                <CustomToastContent
                    content={[
                        t('revisionOverwriteBlockedByPendingResources'),
                        ...blockerMessages,
                    ]}
                />,
                toastOptionsTopRight
            );
            return false;
        }

        return true;
    };

    const refreshSelectedDistributorRevision = async (distributorId: number) => {
        const response = await getInventoryRevision(distributorId);
        setRevision(response);
    };

    const openDistributorRevision = async (distributor: User) => {
        if (!distributor.id) return;

        setSelectedDistributor(distributor);
        setRevision(undefined);
        setIsLoadingRevision(true);
        try {
            await Promise.all([
                refreshSelectedDistributorRevision(distributor.id),
                refreshPendingRevisionDistributorIds(true),
            ]);
        } catch {
            toast.error(<CustomToastContent content={[t('revisionDifferencesLoadError')]} />, toastOptionsTopRight);
        } finally {
            setIsLoadingRevision(false);
        }
    };

    const openOverwriteConfirmModal = async () => {
        if (!selectedDistributor?.id) return;

        setRevisionActionLoading('overwrite');
        try {
            const canOverwrite = await canOverwriteSelectedDistributorRevision(selectedDistributor.id);
            if (canOverwrite) setShowOverwriteConfirm(true);
        } catch {
            toast.error(<CustomToastContent content={[t('revisionSaveError')]} />, toastOptionsTopRight);
        } finally {
            setRevisionActionLoading(undefined);
        }
    };

const handleRevisionAction = async (
    action: 'overwrite' | 'keep',
    differences: InventoryRevisionRow[]
) => {
        if (!selectedDistributor?.id) return;
        const distributorId = selectedDistributor.id;

        setRevisionActionLoading(action);
        try {
            if (action === 'overwrite') {
                const canOverwrite = await canOverwriteSelectedDistributorRevision(distributorId);
                if (!canOverwrite) return;
            }

            const revisionDifferences = createRevisionDifferencePayloads(differences);

            if (action === 'overwrite') {
                await overwriteInventoryRevision(distributorId, revisionDifferences);
            } else {
                await keepInventoryRevision(distributorId, revisionDifferences);
            }

            await refreshSelectedDistributorRevision(distributorId);
            await refreshPendingRevisionDistributorIds(true);
            window.dispatchEvent(new Event(inventoryRevisionObservationsReadEvent));
            setShowOverwriteConfirm(false);
            toast.success(<CustomToastContent content={[t('revisionSaved')]} />, toastOptionsTopRight);
        } catch {
            toast.error(<CustomToastContent content={[t('revisionSaveError')]} />, toastOptionsTopRight);
        } finally {
            setRevisionActionLoading(undefined);
        }
    };

    const openSavedDifferencesModal = async (distributor = selectedDistributor) => {
        if (!distributor?.id) return;

        setHistoryDistributor(distributor);
        setShowSavedDifferences(true);
        setIsLoadingSavedDifferences(true);
        try {
            const records = await getInventoryRevisionDifferences(distributor.id);
            const visibleRecords = sortDifferenceRecords(records).filter(isVisibleDifferenceRecord);
            setSavedDifferences(visibleRecords);
        } catch {
            setSavedDifferences([]);
            toast.error(<CustomToastContent content={[t('revisionDifferencesLoadError')]} />, toastOptionsTopRight);
        } finally {
            setIsLoadingSavedDifferences(false);
        }
    };

    const openDifferenceDocument = async (record: InventoryRevisionDifferenceRecord) => {
        if (isUnreadRevisionObservationRecord(record) && typeof record.id === 'number') {
            try {
                const updatedRecord = await markInventoryRevisionHistoryRead(record.id);
                setSavedDifferences((currentRecords) => currentRecords.map((currentRecord) =>
                    currentRecord.id === updatedRecord.id ? { ...currentRecord, ...updatedRecord } : currentRecord
                ));
                window.dispatchEvent(new Event(inventoryRevisionObservationsReadEvent));
            } catch {
                toast.error(<CustomToastContent content={[t('revisionMarkAsReadError')]} />, toastOptionsTopRight);
            }
        }

        printDifferenceRecord(record, savedDifferencesDistributor?.name ?? '', {
            action: t('action'),
            date: t('date'),
            distributor: t('distributor'),
            difference: t('difference'),
            inventoryAsOfRevisionDate: t('inventoryAsOfRevisionDate'),
            inventoryRevisionDate: getDifferenceRecordDate(record, i18n.language, t('noDate')),
            ml: t('ml'),
            name: t('name'),
            noDifferences: t('noRevisionDifferences'),
            noDate: t('noDate'),
            pcs: t('pcs'),
            report: t('report'),
            revision: t('revision'),
            revisionActionKeep: t('revisionActionKeep'),
            revisionActionOverwrite: t('revisionActionOverwrite'),
            submittedRevisionQuantity: t('submittedRevisionQuantity'),
        }, i18n.language);
    };

    const closeSavedDifferencesModal = () => {
        setShowSavedDifferences(false);
        setHistoryDistributor(undefined);
    };

    const savedDifferencesDistributor = selectedDistributor ?? historyDistributor;
    const savedDifferencesModal = (
        <Modal show={showSavedDifferences} onHide={closeSavedDifferencesModal} centered size='lg'>
            <Modal.Header closeButton>
                <Modal.Title>{t('report')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoadingSavedDifferences ? (
                    <div className='h-100 d-flex align-items-center justify-content-center w-100 py-4'>
                        <Spinner animation='border' />
                    </div>
                ) : savedDifferences.length > 0 ? (
                    <div
                        className='d-flex flex-column gap-2'
                        style={{ maxHeight: '390px', overflowY: 'auto' }}
                    >
                        {savedDifferences.map((record, index) => (
                            <div
                                key={getDifferenceRecordKey(record, index)}
                                className='border bg-white p-3 d-flex justify-content-between align-items-center gap-2 text-start'
                            >
                                <Button
                                    variant='light'
                                    type='button'
                                    className='flex-grow-1 d-flex justify-content-between align-items-center gap-2 text-start'
                                    onClick={() => openDifferenceDocument(record)}
                                >
                                    <span>{getDifferenceRecordDate(record, i18n.language, t('noDate'))}</span>
                                    {record.performedByUserName && (
                                        <span className='text-nowrap'>{record.performedByUserName}</span>
                                    )}
                                </Button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className='border bg-white p-3 text-center'>
                        {t('noSavedRevisionDifferences')}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant='outline-secondary'
                    type='button'
                    onClick={closeSavedDifferencesModal}
                >
                    {t('close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    if (selectedDistributor) {
        const { availableItems, submittedItems } = splitRevisionResponse(revision);
        const hasSubmittedRevision = submittedItems.length > 0;
        const differenceItems = hasSubmittedRevision ? getDifferenceItems(availableItems, submittedItems) : [];
        const rowBackgroundsByKey = getDifferenceRowBackgroundsByKey(differenceItems);
        const alignedRevisionItems = hasSubmittedRevision
            ? getAlignedRevisionItems(availableItems, submittedItems)
            : { availableItems, submittedItems: [] };
        const revisionDate = getRevisionDate(revision, i18n.language);
        const hasPendingSelectedRevision = selectedDistributor.id != null &&
            pendingRevisionDistributorIds.has(selectedDistributor.id);
        const displayedSubmittedItems = hasPendingSelectedRevision ? alignedRevisionItems.submittedItems : [];
        const showSavedDifferencesButton = revision !== undefined;

        return (
            <>
                <div className='d-flex align-items-center justify-content-between gap-3 flex-wrap w-100 mb-3'>
                    <Button
                        variant='outline-secondary'
                        type='button'
                        onClick={() => {
                            setSelectedDistributor(undefined);
                            setRevision(undefined);
                        }}
                    >
                        {t('previous')}
                    </Button>
                    <h2
                        className='mb-0 flex-grow-1 text-center fw-bold text-dark'
                        style={{ fontSize: '2.35rem', lineHeight: 1.15 }}
                    >
                        {selectedDistributor.name}
                        {revisionDate ? ` ${revisionDate}` : ''}
                    </h2>
                    {!isLoadingRevision && (
                        <div className='d-flex gap-2 flex-wrap justify-content-end'>
                            {hasSubmittedRevision && hasPendingSelectedRevision && (
                                <>
                                    <Button
                                        variant='danger'
                                        type='button'
                                        className='position-relative d-inline-flex align-items-center justify-content-center'
                                        disabled={revisionActionLoading !== undefined}
                                        onClick={openOverwriteConfirmModal}
                                    >
                                        {revisionActionLoading === 'overwrite' ? (
                                            <Spinner animation='border' size='sm' className='position-absolute' />
                                        ) : null}
                                        <span className={revisionActionLoading === 'overwrite' ? 'invisible' : ''}>
                                            {t('acceptRevisionReport')}
                                        </span>
                                    </Button>
                                    <Button
                                        variant='outline-secondary'
                                        type='button'
                                        className='position-relative d-inline-flex align-items-center justify-content-center'
                                        disabled={revisionActionLoading !== undefined}
                                        onClick={() => handleRevisionAction('keep', differenceItems)}
                                    >
                                        {revisionActionLoading === 'keep' ? (
                                            <Spinner animation='border' size='sm' className='position-absolute' />
                                        ) : null}
                                        <span className={revisionActionLoading === 'keep' ? 'invisible' : ''}>
                                            {t('rejectRevisionReport')}
                                        </span>
                                    </Button>
                                </>
                            )}
                            {showSavedDifferencesButton && (
                                <Button
                                    variant='outline-primary'
                                    type='button'
                                    onClick={() => openSavedDifferencesModal(selectedDistributor)}
                                >
                                    {t('history')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                {showOverwriteConfirm && <OverwriteInventoryArrow />}
                <Modal show={showOverwriteConfirm} onHide={() => setShowOverwriteConfirm(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('confirmation')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {t('confirmOverwriteInventoryRevision')}
                    </Modal.Body>
                    <Modal.Footer className='d-flex justify-content-between'>
                        <Button
                            variant='outline-secondary'
                            type='button'
                            disabled={revisionActionLoading !== undefined}
                            onClick={() => setShowOverwriteConfirm(false)}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            variant='danger'
                            type='button'
                            className='position-relative d-inline-flex align-items-center justify-content-center'
                            disabled={revisionActionLoading !== undefined}
                            onClick={() => handleRevisionAction('overwrite', differenceItems)}
                        >
                            {revisionActionLoading === 'overwrite' ? (
                                <Spinner animation='border' size='sm' className='position-absolute' />
                            ) : null}
                            <span className={revisionActionLoading === 'overwrite' ? 'invisible' : ''}>
                                {t('overwrite')}
                            </span>
                        </Button>
                    </Modal.Footer>
                </Modal>
                {savedDifferencesModal}
                {isLoadingRevision ? (
                    <div className='h-100 d-flex align-items-center justify-content-center w-100 py-4'>
                        <Spinner animation='border' />
                    </div>
                ) : (
                    <>
                        <div className='d-flex flex-column flex-xl-row gap-3 w-100'>
                            <InventoryRevisionTable
                                title={t('inventory')}
                                rows={alignedRevisionItems.availableItems}
                                rowBackgroundsByKey={rowBackgroundsByKey}
                                sortRows={false}
                            />
                            <InventoryRevisionTable
                                title={t('reported')}
                                rows={displayedSubmittedItems}
                                rowBackgroundsByKey={rowBackgroundsByKey}
                                sortRows={false}
                            />
                            <InventoryRevisionTable
                                title={t('differences')}
                                rows={differenceItems}
                                rowBackgroundsByKey={rowBackgroundsByKey}
                            />
                        </div>
                    </>
                )}
            </>
        );
    }

    if (isLoadingDistributors) {
        return (
            <div className='h-100 d-flex align-items-center justify-content-center w-100 py-4'>
                <Spinner animation='border' />
            </div>
        );
    }

    return items.length > 0 ? (
        <>
            {items.map((distributor, index) => (
                <div
                    key={distributor.id}
                    className='my-2 d-flex align-items-center text-start w-100 border bg-light py-2'
                    role='button'
                    tabIndex={0}
                    style={{ cursor: isLoadingRevision ? 'default' : 'pointer' }}
                    onClick={() => openDistributorRevision(distributor)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openDistributorRevision(distributor);
                        }
                    }}
                >
                    <h4 className='mb-0 px-3'>{index + 1}.</h4>
                    <h4 className='mb-0 px-3 flex-grow-1'>{distributor.name}.</h4>
                    {distributor.id && pendingRevisionDistributorIds.has(distributor.id) && (
                        <FontAwesomeIcon
                            className='text-warning mx-3 warning-triangle-bordered handout-requests-indicator-pulse'
                            icon={faExclamationTriangle}
                            title={t('inventoryRevisionsAvailable')}
                        />
                    )}
                </div>
            ))}
            {savedDifferencesModal}
        </>
    ) : (
        <NoDataComponent noDataText={t('distributors')} />
    );
};

const collectionKeys = {
    available: [
        'inventory',
        'available',
        'availableItems',
        'availableQuantities',
        'real',
        'realQuantities',
        'actual',
        'actualQuantities',
    ],
    submitted: [
        'revision',
        'revizion',
        'submitted',
        'submittedItems',
        'submittedQuantities',
        'listed',
        'listedQuantities',
        'counted',
        'countedQuantities',
    ],
};

const splitRevisionResponse = (response?: InventoryRevisionResponse) => {
    return {
        availableItems: getFirstCollection(response, collectionKeys.available),
        submittedItems: getFirstCollection(response, collectionKeys.submitted),
    };
};

const getFirstCollection = (response: InventoryRevisionResponse | undefined, keys: string[]) => {
    if (!response) return [];

    for (const key of keys) {
        const value = response[key];
        const collection = normalizeRevisionCollection(value);
        if (collection.length > 0) return collection;
    }

    return [];
};

const normalizeRevisionCollection = (value: unknown): InventoryRevisionRow[] => {
    if (Array.isArray(value)) return value as InventoryRevisionRow[];
    if (!value || typeof value !== 'object') return [];

    return Object.entries(value as Record<string, unknown>).reduce<InventoryRevisionRow[]>((rows, [type, items]) => {
        if (!Array.isArray(items)) return rows;

        return [
            ...rows,
            ...items.map((item) => ({
                type,
                ...(item as InventoryRevisionRow),
            })),
        ];
    }, []);
};

export const getDifferenceItems = (availableItems: InventoryRevisionRow[], submittedItems: InventoryRevisionRow[]) => {
    const revisionItems = filterRevisionOverwriteItems(availableItems, submittedItems);
    const sprayWeightItemKeys = getSprayWeightRevisionItemKeys([
        ...revisionItems.availableItems,
        ...revisionItems.submittedItems,
    ]);
    const availableByKey = aggregateRevisionItems(revisionItems.availableItems, sprayWeightItemKeys);
    const submittedByKey = aggregateRevisionItems(revisionItems.submittedItems, sprayWeightItemKeys);
    const allKeys = Array.from(new Set([...Object.keys(availableByKey), ...Object.keys(submittedByKey)]));

    return allKeys
        .map((key) => {
            const available = availableByKey[key];
            const submitted = submittedByKey[key];
            const availableQuantity = available?.quantity ?? 0;
            const submittedQuantity = submitted?.quantity ?? 0;
            const difference = submittedQuantity - availableQuantity;

            return {
                idOfPassport: available?.idOfPassport ?? submitted?.idOfPassport,
                name: available?.name ?? submitted?.name,
                passport: available?.name ?? submitted?.name,
                type: available?.type ?? submitted?.type,
                quantityUnit: available?.unit ?? submitted?.unit,
                totalAmount: available?.totalAmount ?? submitted?.totalAmount,
                trackingType: available?.trackingType ?? submitted?.trackingType,
                consumableTrackingType: available?.consumableTrackingType ?? submitted?.consumableTrackingType,
                passportTrackingType: available?.passportTrackingType ?? submitted?.passportTrackingType,
                priceForSale: available?.priceForSale ?? submitted?.priceForSale,
                price_for_sale: available?.price_for_sale ?? submitted?.price_for_sale,
                priceForInvoice: available?.priceForInvoice ?? submitted?.priceForInvoice,
                price_for_invoice: available?.price_for_invoice ?? submitted?.price_for_invoice,
                availableQuantity,
                count: difference,
                difference,
                passportId: available?.idOfPassport ?? submitted?.idOfPassport,
                submittedQuantity,
            } as InventoryRevisionRow;
        })
        .filter((item) => Number(item.count) !== 0);
};

export const createRevisionDifferencePayloads = (items: InventoryRevisionRow[]) => {
    return filterLiquidOilPcsItems(items).map(createRevisionDifferencePayload);
};

export const createRevisionDifferencePayload = (item: InventoryRevisionRow) => {
    const difference = getRevisionItemQuantity(item);
    const submittedQuantity = Number(item.submittedQuantity);
    const availableQuantity = Number(item.availableQuantity);
    const revisionBatchCode = getGeneratedBatchCode('R');

    return {
        passportId: item.passportId ?? item.idOfPassport,
        name: item.name ?? item.passport,
        type: item.type,
        batchCode: revisionBatchCode,
        batchNumber: revisionBatchCode,
        newBatchCode: revisionBatchCode,
        revisionBatchCode,
        count: Number.isInteger(difference) ? difference : Math.round(difference),
        quantity: difference,
        quantityUnit: item.quantityUnit,
        difference,
        availableQuantity: Number.isNaN(availableQuantity) ? undefined : availableQuantity,
        submittedQuantity: Number.isNaN(submittedQuantity) ? undefined : submittedQuantity,
    };
};

const getAlignedRevisionItems = (availableItems: InventoryRevisionRow[], submittedItems: InventoryRevisionRow[]) => {
    const revisionItems = filterRevisionOverwriteItems(availableItems, submittedItems);
    const sprayWeightItemKeys = getSprayWeightRevisionItemKeys([
        ...revisionItems.availableItems,
        ...revisionItems.submittedItems,
    ]);
    const availableByKey = aggregateRevisionItems(revisionItems.availableItems, sprayWeightItemKeys);
    const submittedByKey = aggregateRevisionItems(revisionItems.submittedItems, sprayWeightItemKeys);
    const sortedKeys = Array.from(new Set([...Object.keys(availableByKey), ...Object.keys(submittedByKey)])).sort((first, second) => {
        return compareRevisionItems(
            createRevisionPlaceholderItem(availableByKey[first] ?? submittedByKey[first]),
            createRevisionPlaceholderItem(availableByKey[second] ?? submittedByKey[second])
        );
    });

    return sortedKeys.reduce<{ availableItems: InventoryRevisionRow[]; submittedItems: InventoryRevisionRow[] }>(
        (alignedItems, key) => {
            const available = availableByKey[key];
            const submitted = submittedByKey[key];

            alignedItems.availableItems.push(
                available ? createAggregatedRevisionItem(available) : createRevisionPlaceholderItem(submitted)
            );
            alignedItems.submittedItems.push(
                submitted ? createAggregatedRevisionItem(submitted) : createRevisionPlaceholderItem(available)
            );

            return alignedItems;
        },
        { availableItems: [], submittedItems: [] }
    );
};

const createAggregatedRevisionItem = (item: AggregatedRevisionItem) => {
    return {
        idOfPassport: item.idOfPassport,
        name: item.name,
        passport: item.name,
        type: item.type,
        quantityUnit: item.unit,
        totalAmount: item.totalAmount,
        trackingType: item.trackingType,
        consumableTrackingType: item.consumableTrackingType,
        passportTrackingType: item.passportTrackingType,
        priceForSale: item.priceForSale,
        price_for_sale: item.price_for_sale,
        priceForInvoice: item.priceForInvoice,
        price_for_invoice: item.price_for_invoice,
        count: item.quantity,
    } as InventoryRevisionRow;
};

const createRevisionPlaceholderItem = (item?: AggregatedRevisionItem) => {
    return {
        idOfPassport: item?.idOfPassport,
        name: item?.name,
        passport: item?.name,
        type: item?.type,
        quantityUnit: item?.unit,
        totalAmount: item?.totalAmount,
        trackingType: item?.trackingType,
        consumableTrackingType: item?.consumableTrackingType,
        passportTrackingType: item?.passportTrackingType,
        priceForSale: item?.priceForSale,
        price_for_sale: item?.price_for_sale,
        priceForInvoice: item?.priceForInvoice,
        price_for_invoice: item?.price_for_invoice,
        count: 0,
        isPlaceholder: true,
    } as InventoryRevisionRow;
};

interface AggregatedRevisionItem {
    idOfPassport?: unknown;
    name: string;
    quantity: number;
    type?: unknown;
    unit: string;
    totalAmount?: unknown;
    trackingType?: unknown;
    consumableTrackingType?: unknown;
    passportTrackingType?: unknown;
    priceForSale?: unknown;
    price_for_sale?: unknown;
    priceForInvoice?: unknown;
    price_for_invoice?: unknown;
}

const aggregateRevisionItems = (
    items: InventoryRevisionRow[],
    sprayWeightItemKeys: Set<string> = new Set()
) => {
    return items.reduce<Record<string, AggregatedRevisionItem>>((aggregated, item) => {
        const key = getRevisionItemKey(item, sprayWeightItemKeys);
        const name = formatCellValue(item.passport ?? item.name);
        const { unit } = getRevisionItemQuantityInfo(item, sprayWeightItemKeys);
        const quantity = getRevisionItemQuantity(item, sprayWeightItemKeys);

        if (!aggregated[key]) {
            aggregated[key] = {
                idOfPassport: item.idOfPassport,
                name,
                quantity: 0,
                type: item.type,
                unit,
                totalAmount: item.totalAmount,
                trackingType: item.trackingType,
                consumableTrackingType: item.consumableTrackingType,
                passportTrackingType: item.passportTrackingType,
                priceForSale: item.priceForSale,
                price_for_sale: item.price_for_sale,
                priceForInvoice: item.priceForInvoice,
                price_for_invoice: item.price_for_invoice,
            };
        }

        aggregated[key].quantity += quantity;
        aggregated[key].trackingType = aggregated[key].trackingType ?? item.trackingType;
        aggregated[key].consumableTrackingType = aggregated[key].consumableTrackingType ?? item.consumableTrackingType;
        aggregated[key].passportTrackingType = aggregated[key].passportTrackingType ?? item.passportTrackingType;
        return aggregated;
    }, {});
};

const getRevisionItemKey = (item: InventoryRevisionRow, sprayWeightItemKeys: Set<string> = new Set()) => {
    const type = formatCellValue(item.type);
    const { unit } = getRevisionItemQuantityInfo(item, sprayWeightItemKeys);
    const idOfPassport = item.idOfPassport;
    if (idOfPassport !== null && idOfPassport !== undefined) return `${type}-${unit}-${idOfPassport}`;

    return `${type}-${unit}-${formatCellValue(item.passport ?? item.name)}`;
};

const getRevisionItemQuantity = (item: InventoryRevisionRow, sprayWeightItemKeys: Set<string> = new Set()) => {
    const { value } = getRevisionItemQuantityInfo(item, sprayWeightItemKeys);
    const quantity = Number(value);

    return Number.isNaN(quantity) ? 0 : quantity;
};

const getRevisionItemQuantityInfo = (item: InventoryRevisionRow, sprayWeightItemKeys: Set<string> = new Set()) => {
    if (isSprayWeightRevisionItem(item) || sprayWeightItemKeys.has(getRevisionItemIdentityKey(item))) {
        return { value: getRevisionItemCountValue(item), unit: 'pcs' };
    }

    const explicitUnit = formatCellValue(item.quantityUnit);
    if (explicitUnit) return { value: getRevisionItemCountValue(item), unit: explicitUnit };

    const isOil = String(item.type).toLowerCase() === 'oil';
    const isEmpty = String(item.state).toLowerCase() === 'empty';
    const hasAvailableAmount = item.availableAmount !== null && item.availableAmount !== undefined && item.availableAmount !== '';

    if (isOil && !isEmpty && hasAvailableAmount) {
        return { value: item.availableAmount, unit: 'ml' };
    }

    return { value: item.count, unit: 'pcs' };
};

const filterRevisionOverwriteItems = (availableItems: InventoryRevisionRow[], submittedItems: InventoryRevisionRow[]) => {
    return {
        availableItems: filterLiquidOilPcsItems(availableItems),
        submittedItems: filterLiquidOilPcsItems(submittedItems),
    };
};

const filterLiquidOilPcsItems = (items: InventoryRevisionRow[]) => {
    return items.filter((item) => !isLiquidOilPcsRevisionItem(item));
};

const isLiquidOilPcsRevisionItem = (item: InventoryRevisionRow) => {
    const type = formatCellValue(item.type).toUpperCase();
    if (type !== 'OIL') return false;
    if (isSprayWeightRevisionItem(item)) return false;

    return getRevisionItemQuantityInfo(item).unit === 'pcs';
};

const getRevisionItemCountValue = (item: InventoryRevisionRow) => {
    const countFields = [
        item.count,
        item.itemCount,
        item.quantity,
        item.countQuantity,
        item.totalCount,
        item.itemsCount,
    ];
    const nonZeroCount = countFields.find((value) => {
        const quantity = Number(value);

        return !Number.isNaN(quantity) && quantity !== 0;
    });

    return nonZeroCount ?? item.count;
};

const getSprayWeightRevisionItemKeys = (items: InventoryRevisionRow[]) => {
    return items.reduce<Set<string>>((keys, item) => {
        if (isSprayWeightRevisionItem(item)) keys.add(getRevisionItemIdentityKey(item));

        return keys;
    }, new Set());
};

const getRevisionItemIdentityKey = (item: InventoryRevisionRow) => {
    const type = formatCellValue(item.type);
    const idOfPassport = item.idOfPassport;
    if (idOfPassport !== null && idOfPassport !== undefined) return `${type}-${idOfPassport}`;

    return `${type}-${formatCellValue(item.passport ?? item.name)}`;
};

const isSprayWeightRevisionItem = (item: InventoryRevisionRow) => {
    const trackingType = formatCellValue(
        item.trackingType ?? item.consumableTrackingType ?? item.passportTrackingType
    ).toUpperCase();

    return trackingType === 'SPRAY_WEIGHT' || trackingType === 'SPRAY_WIGHT' || trackingType === 'SPRAY_WEGHT';
};

const formatRevisionItemQuantity = (
    item: InventoryRevisionRow,
    unitLabels: Record<string, string> = {},
) => {
    const { value, unit } = getRevisionItemQuantityInfo(item);

    return `${formatCellValue(value)} ${unitLabels[unit] ?? unit}`;
};

const formatRevisionItemQuantityValue = (
    item: InventoryRevisionRow,
    value: unknown,
    unitLabels: Record<string, string> = {},
) => {
    if (value === null || value === undefined || value === '') return '';

    const { unit } = getRevisionItemQuantityInfo(item);

    return `${formatCellValue(value)} ${unitLabels[unit] ?? unit}`;
};

const compareRevisionItems = (first: InventoryRevisionRow, second: InventoryRevisionRow) => {
    const firstType = formatCellValue(first.type);
    const secondType = formatCellValue(second.type);
    const typeCompare = firstType.localeCompare(secondType, 'bg', { sensitivity: 'base' });
    if (typeCompare !== 0) return typeCompare;

    const firstName = formatCellValue(first.passport ?? first.name);
    const secondName = formatCellValue(second.passport ?? second.name);
    const nameCompare = firstName.localeCompare(secondName, 'bg', { sensitivity: 'base' });
    if (nameCompare !== 0) return nameCompare;

    const firstUnit = getRevisionItemQuantityInfo(first).unit;
    const secondUnit = getRevisionItemQuantityInfo(second).unit;
    const unitCompare = firstUnit.localeCompare(secondUnit, 'bg', { sensitivity: 'base' });
    if (unitCompare !== 0) return unitCompare;

    return formatCellValue(first.idOfPassport).localeCompare(formatCellValue(second.idOfPassport), 'bg', {
        numeric: true,
    });
};

const revisionDifferenceBackground = '#f8d7da';

const getDifferenceRowBackground = (_item: InventoryRevisionRow) => revisionDifferenceBackground;

const getDifferenceRowBackgroundsByKey = (items: InventoryRevisionRow[]) => {
    return items.reduce<Record<string, string>>((backgroundsByKey, item) => {
        backgroundsByKey[getRevisionItemKey(item)] = getDifferenceRowBackground(item);

        return backgroundsByKey;
    }, {});
};

const InventoryRevisionTable = ({
    title,
    rows,
    rowBackgroundsByKey = {},
    sortRows = true,
}: {
    title: string;
    rows: InventoryRevisionRow[];
    rowBackgroundsByKey?: Record<string, string>;
    sortRows?: boolean;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const sortedRows = sortRows ? [...rows].sort(compareRevisionItems) : rows;

    return (
        <div className='w-100'>
            <h5 className='mb-2'>{title}</h5>
            {rows.length > 0 ? (
                <Table responsive bordered hover className='bg-white mb-2'>
                    <thead className='background-dark color-main'>
                    <tr>
                        <th className='single-line-text table-header text-center fw-bold'>{t('name')}</th>
                        <th className='single-line-text table-header text-center fw-bold'>{t('quantity')}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {sortedRows.map((row, rowIndex) => (
                        <tr
                            key={rowIndex}
                            style={
                                rowBackgroundsByKey[getRevisionItemKey(row)]
                                    ? { backgroundColor: rowBackgroundsByKey[getRevisionItemKey(row)] }
                                    : undefined
                            }
                        >
                            <td className='p-3'>{formatCellValue(row.passport ?? row.name)}</td>
                            <td className='p-3'>{formatRevisionItemQuantity(row, { ml: t('ml'), pcs: t('pcs') })}</td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            ) : (
                <div className='border bg-white p-3 text-center'>{t('noData')}</div>
            )}
        </div>
    );
};

const OverwriteInventoryArrow = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });

    return ReactDOM.createPortal(
        <div
            className='text-center'
            style={{
                position: 'fixed',
                top: 'calc(50% - 315px)',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 'min(430px, calc(100vw - 32px))',
                zIndex: 9999,
                isolation: 'isolate',
                pointerEvents: 'none',
            }}
        >
            <style>
                {`
                    .revision-overwrite-arrow-path {
                        stroke-dasharray: 12 10;
                        animation: revision-overwrite-dash 1.2s linear infinite;
                    }

                    .revision-overwrite-arrow-dot {
                        offset-path: path('M 310 48 C 245 4, 115 4, 50 48');
                        animation: revision-overwrite-dot 1.8s ease-in-out infinite;
                    }

                    @keyframes revision-overwrite-dash {
                        to {
                            stroke-dashoffset: 22;
                        }
                    }

                    @keyframes revision-overwrite-dot {
                        0% {
                            offset-distance: 0%;
                            opacity: 0;
                        }
                        12% {
                            opacity: 1;
                        }
                        88% {
                            opacity: 1;
                        }
                        100% {
                            offset-distance: 100%;
                            opacity: 0;
                        }
                    }
                `}
            </style>
            <div className='d-flex justify-content-between fw-bold mb-1 px-3 bg-white rounded border py-1'>
                <span>{t('inventory')}</span>
                <span>{t('reported')}</span>
            </div>
            <svg viewBox='0 0 360 74' width='100%' height='74' role='img' aria-label={t('reportedToInventory')}>
                <defs>
                    <marker
                        id='revision-overwrite-arrow-head'
                        markerWidth='10'
                        markerHeight='10'
                        refX='9'
                        refY='5'
                        orient='auto'
                    >
                        <path d='M 0 0 L 10 5 L 0 10 z' fill='#dc3545' />
                    </marker>
                </defs>
                <path
                    className='revision-overwrite-arrow-path'
                    d='M 310 48 C 245 4, 115 4, 50 48'
                    fill='none'
                    stroke='#dc3545'
                    strokeWidth='5'
                    strokeLinecap='round'
                    markerEnd='url(#revision-overwrite-arrow-head)'
                />
                <circle className='revision-overwrite-arrow-dot' r='6' fill='#dc3545' />
            </svg>
        </div>,
        document.body
    );
};

const formatCellValue = (value: unknown) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);

    return String(value);
};

const sortDifferenceRecords = (records: InventoryRevisionDifferenceRecord[]) => {
    return [...records].sort((first, second) => {
        return getDifferenceRecordTimestamp(second) - getDifferenceRecordTimestamp(first);
    });
};

const isVisibleDifferenceRecord = (record: InventoryRevisionDifferenceRecord) => {
    return formatCellValue(record.action).toUpperCase() !== 'INVENTORY_REVISION_LOSS';
};

const isRevisionObservationRecord = (record?: InventoryRevisionDifferenceRecord) => {
    if (!record) return false;

    const action = formatCellValue(record.action).toUpperCase();
    return ['OVERWRITE', 'OVERRIDE'].includes(action) && getDifferenceRecordRows(record).length > 0;
};

const isUnreadRevisionObservationRecord = (record?: InventoryRevisionDifferenceRecord) => {
    return isRevisionObservationRecord(record) && !record?.readAt;
};

const getDifferenceRecordTimestamp = (record: InventoryRevisionDifferenceRecord) => {
    const dateValue = getDifferenceRecordDateValue(record);
    if (!dateValue) return 0;

    const date = new Date(String(dateValue));
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const getDifferenceRecordDateValue = (record: InventoryRevisionDifferenceRecord) => {
    return record.createdAt ?? record.createdDate ?? record.revisionDate ?? record.date;
};

const getDifferenceRecordDate = (
    record: InventoryRevisionDifferenceRecord,
    locale = 'bg-BG',
    noDateLabel = 'No date',
) => {
    const dateValue = getDifferenceRecordDateValue(record);
    if (!dateValue) return noDateLabel;

    const date = new Date(String(dateValue));
    if (Number.isNaN(date.getTime())) return formatCellValue(dateValue);

    return date.toLocaleString(locale);
};

const getDifferenceRecordRows = (record: InventoryRevisionDifferenceRecord) => {
    if (Array.isArray(record.differences)) return record.differences;
    if (Array.isArray(record.items)) return record.items;

    return [];
};

const getDifferenceRecordKey = (record: InventoryRevisionDifferenceRecord, index: number) => {
    return formatCellValue(record.id) || `${getDifferenceRecordDate(record)}-${index}`;
};

interface DifferenceRecordActionLabels {
    revision: string;
    revisionActionKeep: string;
    revisionActionOverwrite: string;
}

const formatDifferenceRecordAction = (
    action: unknown,
    labels: DifferenceRecordActionLabels = {
        revision: 'Revision',
        revisionActionKeep: 'Rejected',
        revisionActionOverwrite: 'Overwritten',
    },
) => {
    const normalizedAction = formatCellValue(action).toUpperCase();

    if (normalizedAction === 'KEEP') return labels.revisionActionKeep;
    if (normalizedAction === 'OVERRIDE' || normalizedAction === 'OVERWRITE') return labels.revisionActionOverwrite;

    return formatCellValue(action) || labels.revision;
};

interface DifferenceRecordPrintLabels extends DifferenceRecordActionLabels {
    action: string;
    date: string;
    difference: string;
    distributor: string;
    inventoryAsOfRevisionDate: string;
    inventoryRevisionDate: string;
    ml: string;
    name: string;
    noDifferences: string;
    noDate: string;
    pcs: string;
    report: string;
    submittedRevisionQuantity: string;
}

const printDifferenceRecord = (
    record: InventoryRevisionDifferenceRecord,
    distributorName: string | undefined,
    labels: DifferenceRecordPrintLabels,
    locale: string,
) => {
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);

    const printWindow = printFrame.contentWindow;
    const printDocument = printFrame.contentDocument ?? printWindow?.document;
    if (!printWindow || !printDocument) {
        printFrame.remove();
        return;
    }

    const removeFrame = () => {
        setTimeout(() => printFrame.remove(), 0);
    };

    printWindow.onafterprint = removeFrame;
    printDocument.open();
    printDocument.write(getDifferenceRecordPrintHtml(record, distributorName, labels, locale));
    printDocument.close();
    printWindow.focus();
    setTimeout(() => {
        printWindow.print();
        setTimeout(removeFrame, 1000);
    }, 250);
};

const getDifferenceRecordPrintHtml = (
    record: InventoryRevisionDifferenceRecord,
    distributorName: string | undefined,
    labels: DifferenceRecordPrintLabels,
    locale: string,
) => {
    const rows = getDifferenceRecordRows(record);
    const rowsHtml = rows
        .sort(compareRevisionItems)
        .map((row) => {
            const unitLabels = { ml: labels.ml, pcs: labels.pcs };

            return `
                <tr class="difference-row">
                    <td>${escapeHtml(formatCellValue(row.passport ?? row.name))}</td>
                    <td class="quantity-column">${escapeHtml(formatRevisionItemQuantityValue(row, row.availableQuantity, unitLabels))}</td>
                    <td class="quantity-column">${escapeHtml(formatRevisionItemQuantityValue(row, row.submittedQuantity, unitLabels))}</td>
                    <td class="quantity-column">${escapeHtml(formatRevisionItemQuantity(row, unitLabels))}</td>
                </tr>
            `;
        })
        .join('');

    return `
        <!doctype html>
        <html>
            <head>
                <title>${escapeHtml(labels.report)}</title>
                <style>
                    @page {
                        margin: 0;
                    }
                    html {
                        margin: 0;
                    }
                    body {
                        font-family: Arial, sans-serif;
                        color: #111;
                        margin: 0;
                        padding: 16mm;
                    }
                    h1 {
                        margin: 0 0 8px;
                        font-size: 24px;
                    }
                    .meta {
                        margin-bottom: 20px;
                        font-size: 14px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        table-layout: fixed;
                    }
                    th,
                    td {
                        border: 1px solid #999;
                        padding: 8px;
                        text-align: left;
                    }
                    th {
                        background: #eee;
                    }
                    .difference-row td {
                        background: ${revisionDifferenceBackground};
                    }
                    .quantity-column {
                        text-align: center;
                    }
                </style>
            </head>
            <body>
                <h1>${escapeHtml(labels.report)}</h1>
                <div class="meta">
                    <div><strong>${escapeHtml(labels.distributor)}:</strong> ${escapeHtml(formatCellValue(distributorName))}</div>
                    <div><strong>${escapeHtml(labels.date)}:</strong> ${escapeHtml(getDifferenceRecordDate(record, locale, labels.noDate))}</div>
                    <div><strong>${escapeHtml(labels.action)}:</strong> ${escapeHtml(formatDifferenceRecordAction(record.action, labels))}</div>
                </div>
                <table>
                    <colgroup>
                        <col style="width: 40%;" />
                        <col style="width: 22%;" />
                        <col style="width: 22%;" />
                        <col style="width: 16%;" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>${escapeHtml(labels.name)}</th>
                            <th class="quantity-column">${escapeHtml(labels.inventoryAsOfRevisionDate)}<br>${escapeHtml(labels.inventoryRevisionDate)}</th>
                            <th class="quantity-column">${escapeHtml(labels.submittedRevisionQuantity)}</th>
                            <th class="quantity-column">${escapeHtml(labels.difference)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rowsHtml || `<tr><td colspan="4">${escapeHtml(labels.noDifferences)}</td></tr>`}
                    </tbody>
                </table>
            </body>
        </html>
    `;
};

const escapeHtml = (value: string) => {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
};

const getRevisionDate = (response?: InventoryRevisionResponse, locale = 'bg-BG') => {
    if (!response) return '';

    const dateValue =
        response.revisionDate ??
        response.date ??
        response.createdAt ??
        response.createdDate ??
        response.updatedAt ??
        response.updatedDate;

    if (!dateValue) return '';

    const date = new Date(String(dateValue));
    if (Number.isNaN(date.getTime())) return formatCellValue(dateValue);

    return date.toLocaleDateString(locale);
};
